import React, { useEffect, useState } from 'react';
// import { createPortal } from 'react-dom';
import {
    X, Loader2, Cctv, Network, Server, Monitor, Wifi, HardDrive, Printer, Shield, Router, Box, EthernetPort,
    Hash, Cpu, ShieldCheck, Activity, Globe, Clock, CheckCircle2, XCircle,
    Zap, Unplug, Fingerprint, Share2, Layers, Info,
    MapPin
} from 'lucide-react';
import baseapi from '../../../services/baseapi';
import { useClientLevel } from '../../../hooks/useClientLevel';
import { showSuccess, showError } from '../../../utils/toast';

// ─── Types ──────────────────────────────────────────────────────────────────────

interface AssetDetailSidebarProps {
    open: boolean;
    assetId: number;
    assetName: string;
    assetType: string;
    assetIp: string;
    onClose: () => void;
}

type LucideIcon = React.FC<any>;

const getAssetIcon = (type: string): LucideIcon => {
    const icons: Record<string, LucideIcon> = {
        camera: Cctv, server: Server, workstation: Monitor, access_point: Wifi,
        nvr: HardDrive, printer: Printer, firewall: Shield, router: Router,
        switch: Router, storage: HardDrive, other: Box, local_asset: Server, isp: Wifi,
        hub: EthernetPort,
    };
    return icons[type] || Box;
};

// ─── Compact Card ───────────────────────────────────────────────────────────────

const DetailCard: React.FC<{ icon: LucideIcon; label: string; value: any; status?: boolean }> = ({ icon: Icon, label, value, status }) => (
    <div className="bg-gray-50 dark:bg-gray-800/60 p-2 rounded-lg border border-gray-200 dark:border-gray-700/50 hover:border-gray-300 dark:hover:border-gray-600 transition-all group">
        <div className="flex items-center gap-2">
            <div className="p-1 bg-gray-200 dark:bg-gray-900/80 rounded-md group-hover:bg-orange-600/80 transition-colors shrink-0">
                <Icon className="w-3 h-3 text-orange-500 group-hover:text-white" />
            </div>
            <div className="flex-1 min-w-0">
                <p className="text-[9px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-tight leading-none mb-0.5">{label}</p>
                {status !== undefined ? (
                    <div className="flex items-center gap-1">
                        {status ? <CheckCircle2 className="w-2.5 h-2.5 text-green-500" /> : <XCircle className="w-2.5 h-2.5 text-red-500" />}
                        <span className={`text-[10px] font-bold leading-none ${status ? 'text-green-500' : 'text-red-500'}`}>
                            {value}
                        </span>
                    </div>
                ) : (
                    <p className="text-[10px] font-bold text-gray-800 dark:text-gray-200 truncate leading-none" title={value?.toString()}>
                        {value || "-"}
                    </p>
                )}
            </div>
        </div>
    </div>
);

// ─── Mini Table ─────────────────────────────────────────────────────────────────

const MiniTable: React.FC<{
    icon: LucideIcon;
    title: string;
    iconColor: string;
    columns: { key: string; label: string }[];
    data: any[];
    loading: boolean;
    itemsPerPage?: number;
    renderCell?: (key: string, item: any) => React.ReactNode;
}> = ({ icon: Icon, title, iconColor, columns, data, loading, itemsPerPage = 10, renderCell }) => {
    const [currentPage, setCurrentPage] = React.useState(1);
    const totalPages = Math.ceil(data.length / itemsPerPage);
    const start = (currentPage - 1) * itemsPerPage;
    const pagedData = data.slice(start, start + itemsPerPage);

    // Reset to page 1 when data changes
    React.useEffect(() => { setCurrentPage(1); }, [data]);

    return (
        <div className="overflow-hidden">
            <div className="px-2 py-1 flex items-center gap-1.5">
                <Icon className={`w-3 h-3 ${iconColor}`} />
                <h3 className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest">{title}</h3>
                {data.length > 0 && (
                    <span className="ml-auto text-[9px] text-gray-400 dark:text-gray-600 font-mono">{data.length}</span>
                )}
            </div>
            <div className="bg-gray-50 dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700/40 rounded-lg overflow-hidden">
                <div className="overflow-x-auto custom-scroll">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-gray-100 dark:bg-gray-900/50 border-b border-gray-200 dark:border-gray-700/50">
                                {columns.map((col) => (
                                    <th key={col.key} className="px-2 py-1 text-[9px] text-gray-600 dark:text-gray-500 uppercase tracking-wider font-semibold">
                                        {col.label}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-gray-700/30">
                            {loading ? (
                                <tr>
                                    <td colSpan={columns.length} className="py-4 text-center">
                                        <Loader2 className="w-3.5 h-3.5 text-orange-500 animate-spin mx-auto" />
                                    </td>
                                </tr>
                            ) : data.length === 0 ? (
                                <tr>
                                    <td colSpan={columns.length} className="py-3 text-center text-gray-400 dark:text-gray-600 text-[9px]">
                                        No data
                                    </td>
                                </tr>
                            ) : (
                                pagedData.map((item, i) => (
                                    <tr key={item.id || start + i} className="hover:bg-gray-100 dark:hover:bg-gray-700/20 transition-colors">
                                        {columns.map((col) => (
                                            <td key={col.key} className="px-2 py-1 text-[10px] text-gray-700 dark:text-gray-300">
                                                {renderCell ? renderCell(col.key, item) : (item[col.key] ?? '-')}
                                            </td>
                                        ))}
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
                {totalPages > 1 && (
                    <div className="bg-gray-100/60 dark:bg-gray-900/40 px-2 py-1 border-t border-gray-200 dark:border-gray-700/30 flex justify-between items-center">
                        <span className="text-[9px] text-gray-600 dark:text-gray-200 font-medium">
                            {start + 1}–{Math.min(start + itemsPerPage, data.length)} of {data.length}
                        </span>
                        <div className="flex items-center gap-1">
                            <button
                                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                disabled={currentPage === 1}
                                className="p-0.5 text-gray-500 hover:text-gray-800 dark:text-gray-200 dark:hover:text-white disabled:opacity-30 transition-colors"
                            >
                                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                            </button>
                            <div className="bg-orange-600/10 border border-orange-500/20 text-orange-500 px-1.5 py-0.5 rounded text-[9px] font-bold min-w-[18px] text-center">
                                {currentPage}
                            </div>
                            <button
                                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                disabled={currentPage === totalPages}
                                className="p-0.5 text-gray-500 hover:text-gray-800 dark:text-gray-200 dark:hover:text-white disabled:opacity-30 transition-colors"
                            >
                                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

// ─── API-Paginated Mini Table ───────────────────────────────────────────────────

const ApiMiniTable: React.FC<{
    icon: LucideIcon;
    title: string;
    iconColor: string;
    columns: { key: string; label: string }[];
    endpoint: string;
    query: Record<string, any>;
    itemsPerPage?: number;
    renderCell?: (key: string, item: any) => React.ReactNode;
}> = ({ icon: Icon, title, iconColor, columns, endpoint, query, itemsPerPage = 10, renderCell }) => {
    const [data, setData] = useState<any[]>([]);
    const [totalItems, setTotalItems] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [loading, setLoading] = useState(true);

    const fetchData = React.useCallback(async (page: number) => {
        setLoading(true);
        try {
            const skip = (page - 1) * itemsPerPage;
            const res = await baseapi.get(endpoint, {
                params: { query: JSON.stringify(query), skip, limit: itemsPerPage }
            });
            if (res.data.status) {
                setData(res.data.data || []);
                setTotalItems(res.data.total || res.data.data?.length || 0);
            }
        } catch { /* silent */ }
        finally { setLoading(false); }
    }, [endpoint, JSON.stringify(query), itemsPerPage]);

    useEffect(() => {
        setCurrentPage(1);
        fetchData(1);
    }, [fetchData]);

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
        fetchData(page);
    };

    const totalPages = Math.ceil(totalItems / itemsPerPage);
    const start = (currentPage - 1) * itemsPerPage;

    return (
        <div className="overflow-hidden">
            <div className="px-2 py-1 flex items-center gap-1.5">
                <Icon className={`w-3 h-3 ${iconColor}`} />
                <h3 className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest">{title}</h3>
                {totalItems > 0 && (
                    <span className="ml-auto text-[9px] text-gray-400 dark:text-gray-600 font-mono">{totalItems}</span>
                )}
            </div>
            <div className="bg-gray-50 dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700/40 rounded-lg overflow-hidden">
                <div className="overflow-x-auto custom-scroll">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-gray-100 dark:bg-gray-900/50 border-b border-gray-200 dark:border-gray-700/50">
                                {columns.map((col) => (
                                    <th key={col.key} className="px-2 py-1 text-[9px] text-gray-600 dark:text-gray-500 uppercase tracking-wider font-semibold">
                                        {col.label}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-gray-700/30">
                            {loading ? (
                                <tr>
                                    <td colSpan={columns.length} className="py-4 text-center">
                                        <Loader2 className="w-3.5 h-3.5 text-orange-500 animate-spin mx-auto" />
                                    </td>
                                </tr>
                            ) : data.length === 0 ? (
                                <tr>
                                    <td colSpan={columns.length} className="py-3 text-center text-gray-400 dark:text-gray-600 text-[9px]">
                                        No data
                                    </td>
                                </tr>
                            ) : (
                                data.map((item, i) => (
                                    <tr key={item.id || i} className="hover:bg-gray-100 dark:hover:bg-gray-700/20 transition-colors">
                                        {columns.map((col) => (
                                            <td key={col.key} className="px-2 py-1 text-[10px] text-gray-700 dark:text-gray-300">
                                                {renderCell ? renderCell(col.key, item) : (item[col.key] ?? '-')}
                                            </td>
                                        ))}
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
                {totalPages > 1 && (
                    <div className="bg-gray-100/60 dark:bg-gray-900/40 px-2 py-1 border-t border-gray-200 dark:border-gray-700/30 flex justify-between items-center">
                        <span className="text-[9px] text-gray-600 dark:text-gray-500 font-medium">
                            {start + 1}–{Math.min(start + itemsPerPage, totalItems)} of {totalItems}
                        </span>
                        <div className="flex items-center gap-1">
                            <button
                                onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                                disabled={currentPage === 1}
                                className="p-0.5 text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-white disabled:opacity-30 transition-colors"
                            >
                                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                            </button>
                            <div className="bg-orange-600/10 border border-orange-500/20 text-orange-500 px-1.5 py-0.5 rounded text-[9px] font-bold min-w-[18px] text-center">
                                {currentPage}
                            </div>
                            <button
                                onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                                disabled={currentPage === totalPages}
                                className="p-0.5 text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-white disabled:opacity-30 transition-colors"
                            >
                                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

// ─── Camera Sidebar Content ─────────────────────────────────────────────────────

const CameraSidebarContent: React.FC<{ assetId: number, client_id: any }> = ({ assetId, client_id }) => {
    const [overview, setOverview] = useState<any>(null);
    const [loadingOverview, setLoadingOverview] = useState(true);

    useEffect(() => {
        const query = JSON.stringify({ asset_id: assetId, client_id });

        baseapi.get('/asset_camera_details', { params: { query } })
            .then(res => {
                if (res.data.status && res.data.data?.length > 0) setOverview(res.data.data[0]);
            })
            .catch(() => { })
            .finally(() => setLoadingOverview(false));
    }, [assetId]);

    const formatDate = (dateString: string) => {
        if (!dateString || dateString === '-') return '-';
        try {
            const normalized = /[zZ]|[+-]\d{2}:\d{2}$/.test(dateString) ? dateString : `${dateString}Z`;
            const date = new Date(normalized);
            if (isNaN(date.getTime())) return dateString;
            return date.toLocaleString('en-IN', {
                year: 'numeric', month: 'short', day: 'numeric',
                hour: '2-digit', minute: '2-digit', hour12: true,
            });
        } catch { return dateString; }
    };

    return (
        <div className="space-y-3">
            {/* Overview Cards */}
            {loadingOverview ? (
                <div className="flex justify-center py-4">
                    <Loader2 className="w-4 h-4 text-orange-500 animate-spin" />
                </div>
            ) : overview ? (
                <div className="grid grid-cols-2 gap-1.5">
                    <DetailCard icon={Hash} label="Serial" value={overview.serial_number} />
                    <DetailCard icon={Cpu} label="Firmware" value={overview.firmware} />
                    <DetailCard icon={ShieldCheck} label="Auth" value={overview.auth_success ? 'Success' : 'Failed'} status={overview.auth_success} />
                    <DetailCard icon={Activity} label="Status" value={overview.reachable ? 'Online' : 'Offline'} status={overview.reachable} />
                    <DetailCard icon={Wifi} label="DHCP" value={overview.dhcp === 'True' ? 'On' : 'Off'} status={overview.dhcp === 'True'} />
                    <DetailCard icon={Globe} label="ONVIF" value={overview.onvif_available ? 'Yes' : 'No'} status={overview.onvif_available} />
                    <DetailCard icon={Server} label="Method" value={overview.auth_method} />
                    <DetailCard icon={Clock} label="Updated" value={formatDate(overview.updated)} />
                </div>
            ) : (
                <div className="text-center py-3">
                    <Info className="w-5 h-5 text-gray-600 mx-auto mb-1" />
                    <p className="text-[10px] text-gray-500">No overview data</p>
                </div>
            )}

            {/* RTSP Streams */}
            {/* {overview?.streams && (() => {
                let parsed: any[] = [];
                try { parsed = JSON.parse(overview.streams || '[]'); } catch { }
                return parsed.length > 0 ? (
                    <div>
                        <div className="px-2 py-1 flex items-center gap-1.5">
                            <PlayCircle className="w-3 h-3 text-orange-500" />
                            <h3 className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">RTSP Streams</h3>
                        </div>
                        <div className="space-y-1">
                            {parsed.map((s: any, i: number) => (
                                <div key={i} className="bg-gray-800/40 border border-gray-700/40 rounded-lg p-1.5">
                                    <p className="text-[8px] text-gray-500 uppercase font-bold mb-0.5">{s.stream} Stream</p>
                                    <p className="text-[9px] text-blue-400 break-all font-mono leading-tight">{s.rtsp_url}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                ) : null;
            })()} */}

            {/* Interfaces Table */}
            <ApiMiniTable
                icon={Monitor}
                title="Interfaces"
                iconColor="text-emerald-500"
                endpoint="/asset_camera_interfaces"
                query={{ asset_id: assetId, client_id }}
                columns={[
                    { key: 'name', label: 'Name' },
                    { key: 'enabled', label: 'Status' },
                    { key: 'ipv4_address', label: 'IP' },
                    { key: 'mac_address', label: 'MAC' },
                ]}
                renderCell={(key, item) => {
                    if (key === 'enabled') {
                        return (
                            <span className={`inline-flex items-center px-1 py-0.5 rounded text-[8px] font-bold uppercase ${item.enabled
                                ? 'bg-green-500/10 text-green-500 border border-green-500/20'
                                : 'bg-red-500/10 text-red-500 border border-red-500/20'
                                }`}>
                                {item.enabled ? 'Up' : 'Down'}
                            </span>
                        );
                    }
                    return <span className={key === 'ipv4_address' ? 'text-blue-400 font-mono text-[9px]' : ''}>{item[key] || '-'}</span>;
                }}
            />

            {/* Protocols Table */}
            <ApiMiniTable
                icon={ShieldCheck}
                title="Protocols"
                iconColor="text-blue-500"
                endpoint="/asset_camera_protocols"
                query={{ asset_id: assetId, client_id }}
                columns={[
                    { key: 'name', label: 'Name' },
                    { key: 'enabled', label: 'Status' },
                    { key: 'ports', label: 'Ports' },
                ]}
                renderCell={(key, item) => {
                    if (key === 'enabled') {
                        return (
                            <span className={`inline-flex items-center px-1 py-0.5 rounded text-[8px] font-bold uppercase ${item.enabled
                                ? 'bg-green-500/10 text-green-500 border border-green-500/20'
                                : 'bg-red-500/10 text-red-500 border border-red-500/20'
                                }`}>
                                {item.enabled ? 'On' : 'Off'}
                            </span>
                        );
                    }
                    if (key === 'ports') return <span className="text-blue-400 font-mono text-[9px]">{item[key] || '-'}</span>;
                    return item[key] || '-';
                }}
            />

            {/* Users Table */}
            <ApiMiniTable
                icon={Info}
                title="Access Control"
                iconColor="text-purple-500"
                endpoint="/asset_camera_users"
                query={{ asset_id: assetId, client_id }}
                columns={[
                    { key: 'username', label: 'User' },
                    { key: 'user_level', label: 'Level' },
                    { key: 'auth_success', label: 'Auth' },
                ]}
                renderCell={(key, item) => {
                    if (key === 'auth_success') {
                        if (item.auth_success === null) return <span className="text-[9px] text-gray-500 font-bold">Pending</span>;
                        return item.auth_success
                            ? <span className="text-[9px] text-green-500 font-bold flex items-center gap-0.5"><CheckCircle2 className="w-2.5 h-2.5" />OK</span>
                            : <span className="text-[9px] text-red-500 font-bold flex items-center gap-0.5"><XCircle className="w-2.5 h-2.5" />Fail</span>;
                    }
                    if (key === 'user_level') {
                        return (
                            <span className="px-1 py-0.5 rounded text-[8px] font-bold uppercase bg-blue-500/10 text-blue-400 border border-blue-500/20">
                                {item.user_level || 'unknown'}
                            </span>
                        );
                    }
                    return item[key] || '-';
                }}
            />
        </div>
    );
};

// ─── Firewall Sidebar Content ───────────────────────────────────────────────────

export const FirewallSidebarContent: React.FC<{ assetId: number; client_id?: any }> = ({ assetId, client_id }) => {
    const [deviceInfo, setDeviceInfo] = useState<any>(null);
    const [interfaces, setInterfaces] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        const queryObj: any = {};
        if (assetId) queryObj["asset_id"] = assetId;
        if (client_id) queryObj["client_id"] = client_id;

        baseapi.get("/firewall_interfaces", {
            params: {
                query: JSON.stringify(queryObj),
                limit: 500
            }
        })
            .then(res => {
                if (res.data?.status) {
                    let devInfo = res.data.device_info;
                    if (typeof devInfo === 'string') {
                        try { devInfo = JSON.parse(devInfo); } catch { devInfo = {}; }
                    }
                    setDeviceInfo(devInfo || {});

                    const rawList = res.data.data || [];
                    const formatted = rawList.map((item: any) => {
                        let d: any = {};
                        if (typeof item.data === 'string') {
                            try { d = JSON.parse(item.data); } catch { d = {}; }
                        } else if (typeof item.data === 'object' && item.data !== null) {
                            d = item.data;
                        }
                        return {
                            id: item.id,
                            name: d.name || item.name || '-',
                            interface: d.name || item.name || '-',
                            alias: d.alias || '',
                            type: d.type || '-',
                            ip_address: d.ip_address || '-',
                            link: d.link_status || item.status || '-',
                            role: d.additional_info?.role || ''
                        };
                    });
                    setInterfaces(formatted);
                }
            })
            .catch(() => { })
            .finally(() => setLoading(false));
    }, [assetId, client_id]);

    return (
        <div className="space-y-3">
            {/* Overview Cards from device_info */}
            {loading ? (
                <div className="flex justify-center py-4">
                    <Loader2 className="w-4 h-4 text-orange-500 animate-spin" />
                </div>
            ) : deviceInfo ? (
                <div className="grid grid-cols-2 gap-1.5">
                    <DetailCard icon={Network} label="Model" value={deviceInfo.model || deviceInfo.model_number} />
                    <DetailCard icon={Hash} label="Serial" value={deviceInfo.serial_number} />
                    <DetailCard icon={Cpu} label="OS Version" value={deviceInfo.os_version || deviceInfo.os_full_name} />
                    <DetailCard icon={Clock} label="Uptime" value={deviceInfo.uptime_str || (deviceInfo.uptime ? `${deviceInfo.uptime}s` : "-")} />
                    <DetailCard icon={Globe} label="IP Address" value={deviceInfo.management_ip} />
                    <DetailCard icon={Shield} label="Firewall Type" value={deviceInfo.firewall_type} />
                    <DetailCard icon={Activity} label="Vendor" value={deviceInfo.vendor} />
                    <DetailCard icon={Server} label="Hostname" value={deviceInfo.hostname} />
                </div>
            ) : (
                <div className="text-center py-3">
                    <Info className="w-5 h-5 text-gray-600 mx-auto mb-1" />
                    <p className="text-[10px] text-gray-500">No device info data</p>
                </div>
            )}

            {/* Interfaces Table with Local Pagination */}
            <MiniTable
                icon={Unplug}
                title="Interfaces"
                iconColor="text-blue-500"
                itemsPerPage={10}
                columns={[
                    { key: 'interface', label: 'Interface' },
                    { key: 'type', label: 'Type' },
                    { key: 'ip_address', label: 'IP Address' },
                    { key: 'link', label: 'Link' },
                ]}
                data={interfaces}
                loading={loading}
                renderCell={(key, item) => {
                    if (key === 'link') {
                        const isUp = item.link?.toLowerCase() === 'up';
                        return (
                            <span className={`inline-flex px-1.5 py-0.5 rounded text-[8px] font-bold uppercase ${isUp
                                ? 'bg-green-500/10 text-green-500 border border-green-500/20'
                                : 'bg-red-500/10 text-red-500 border border-red-500/20'
                                }`}>
                                {item.link || '-'}
                            </span>
                        );
                    }
                    if (key === 'interface') {
                        return (
                            <div>
                                <span className="font-bold text-white text-[10px]">{item.interface}</span>
                                {item.alias && (
                                    <span className="block text-[8px] text-gray-400 font-mono leading-tight truncate max-w-[90px]" title={item.alias}>
                                        {item.alias}
                                    </span>
                                )}
                            </div>
                        );
                    }
                    if (key === 'type') {
                        return (
                            <span className="inline-flex px-1 py-0.5 rounded text-[8px] font-bold uppercase bg-blue-500/10 text-blue-400 border border-blue-500/20">
                                {item.type || '-'}
                            </span>
                        );
                    }
                    if (key === 'ip_address') {
                        return (
                            <span className="text-blue-400 font-mono text-[9px]">
                                {item.ip_address || '-'}
                            </span>
                        );
                    }
                    return item[key] ?? '-';
                }}
            />
        </div>
    );
};

// ─── Switch Sidebar Content ─────────────────────────────────────────────────────
const formatBytes = (bytes: number | null) => {
    if (!bytes) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB", "TB", "PB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

const SwitchSidebarContent: React.FC<{ assetId: number }> = ({ assetId }) => {
    const [overview, setOverview] = useState<any>(null);
    const [poe, setPoe] = useState<any[]>([]);
    const [ports, setPorts] = useState<any[]>([]);
    const [loading, setLoading] = useState({
        overview: true, poe: true, ports: true
    });

    useEffect(() => {
        const query = JSON.stringify({ asset_id: assetId });

        baseapi.get('/switch_details', { params: { query } })
            .then(res => {
                if (res.data.status) {
                    setOverview(res.data.data?.[0] || res.data.data);
                }
            })
            .catch(() => { })
            .finally(() => setLoading(prev => ({ ...prev, overview: false })));

        baseapi.get('/poe_info', { params: { query, sort_by: 'created', sort_order: 'desc', limit: 1 } })
            .then(res => {
                if (res.data.status && res.data.data?.length > 0) {
                    const latest = res.data.data[0];
                    try {
                        const parsed = typeof latest.poe_data === 'string'
                            ? JSON.parse(latest.poe_data)
                            : (latest.poe_data || []);
                        setPoe(parsed);
                    } catch { setPoe([]); }
                }
            })
            .catch(() => { })
            .finally(() => setLoading(prev => ({ ...prev, poe: false })));

        baseapi.get('/ports', { params: { query } })
            .then(res => {
                if (res.data.status && res.data.data?.length > 0) {
                    const latestEntry = res.data.data[0];
                    try {
                        const parsedPorts = typeof latestEntry.ports === 'string'
                            ? JSON.parse(latestEntry.ports)
                            : (latestEntry.ports || []);
                        setPorts(parsedPorts);
                    } catch { setPorts([]); }
                }
            })
            .catch(() => { })
            .finally(() => setLoading(prev => ({ ...prev, ports: false })));
    }, [assetId]);

    return (
        <div className="space-y-3">
            {/* Overview Cards */}
            {loading.overview ? (
                <div className="flex justify-center py-4">
                    <Loader2 className="w-4 h-4 text-orange-500 animate-spin" />
                </div>
            ) : overview ? (
                <div className="grid grid-cols-2 gap-1.5">
                    <DetailCard icon={Network} label="Model" value={overview.model} />
                    <DetailCard icon={Hash} label="Serial" value={overview.serial_number} />
                    <DetailCard icon={Cpu} label="IOS Version" value={overview.ios_version} />
                    <DetailCard icon={Clock} label="Uptime" value={overview.uptime || "-"} />
                    <DetailCard icon={Globe} label="IP Address" value={overview.ip_address} />
                    <DetailCard icon={Layers} label="VLANs" value={overview.total_vlans} />
                    <DetailCard icon={Activity} label="Ports Up" value={`${overview.total_ports_up || 0} / ${overview.total_interfaces || 0}`} />
                    <DetailCard icon={Fingerprint} label="MAC Entries" value={overview.total_mac_entries} />
                    <DetailCard icon={MapPin} label="Location" value={overview.location} />
                </div>
            ) : (
                <div className="text-center py-3">
                    <Info className="w-5 h-5 text-gray-600 mx-auto mb-1" />
                    <p className="text-[10px] text-gray-500">No overview data</p>
                </div>
            )}

            {/* PoE Info */}
            <MiniTable
                icon={Zap}
                title="PoE Details"
                iconColor="text-orange-500"
                columns={[
                    { key: 'port', label: 'Port' },
                    { key: 'detection_status', label: 'Detection' },
                    { key: 'max_power_class', label: 'Limit' },
                    { key: 'power_consumption_watts', label: 'Usage' },
                ]}
                data={poe}
                loading={loading.poe}
                renderCell={(key, item) => {
                    if (key === 'is_delivering_power') {
                        return (
                            <span className={`inline-flex px-1 py-0.5 rounded text-[8px] font-bold uppercase ${item.is_delivering_power
                                ? 'bg-green-500/10 text-green-500 border border-green-500/20'
                                : 'bg-gray-100 dark:bg-gray-700/50 text-gray-600 dark:text-gray-500 border border-gray-300 dark:border-gray-600/20'
                                }`}>
                                {item.is_delivering_power ? 'Yes' : 'No'}
                            </span>
                        );
                    }
                    if (key === 'detection_status') {
                        const val = item[key];
                        return (
                            <span className={`inline-flex px-1 py-0.5 rounded text-[8px] font-bold uppercase ${val?.toLowerCase()?.includes('good') || val?.toLowerCase()?.includes('delivering')
                                ? 'bg-green-500/10 text-green-500 border border-green-500/20'
                                : 'bg-gray-100 dark:bg-gray-700/50 text-gray-600 dark:text-gray-500 border border-gray-300 dark:border-gray-600/20'
                                }`}>
                                {val || '-'}
                            </span>
                        );
                    }
                    return item[key] ?? '-';
                }}
            />

            {/* Interface Details - API paginated */}
            <ApiMiniTable
                icon={Unplug}
                title="Interfaces"
                iconColor="text-blue-500"
                endpoint="/discovered_interfaces"
                query={{ switch_asset_id: assetId, interface_type: "ethernetCsmacd", }}
                columns={[
                    { key: 'port_name', label: 'Port' },
                    { key: 'port_status', label: 'Status' },
                    { key: 'port_speed', label: 'Speed' },
                    { key: 'vlan', label: 'VLAN' },
                ]}
                renderCell={(key, item) => {
                    if (key === 'port_status') {
                        const isUp = item.port_status?.toLowerCase() === 'up' || item.port_status?.toLowerCase() === 'connected';
                        return (
                            <span className={`inline-flex px-1 py-0.5 rounded text-[8px] font-bold uppercase ${isUp
                                ? 'bg-green-500/10 text-green-500 border border-green-500/20'
                                : 'bg-red-500/10 text-red-500 border border-red-500/20'
                                }`}>
                                {item.port_status || '-'}
                            </span>
                        );
                    }
                    if (key === 'port_name') return <span className="font-bold text-gray-900 dark:text-white">{item.port_name || item.port_number || '-'}</span>;
                    return item[key] ?? '-';
                }}
            />

            {/* MAC Address Entries - API paginated */}
            <ApiMiniTable
                icon={Fingerprint}
                title="MAC Entries"
                iconColor="text-orange-500"
                endpoint="/mac_table"
                query={{ asset_id: assetId }}
                columns={[
                    { key: 'mac_address', label: 'MAC' },
                    { key: 'bridge_port', label: 'Port' },
                    { key: 'vlan', label: 'VLAN' },
                    { key: 'vendor', label: 'Vendor' },
                ]}
                renderCell={(key, item) => {
                    if (key === 'mac_address') return <span className="font-mono text-blue-400 text-[9px]">{item[key] || '-'}</span>;
                    if (key === 'vlan') return <span className="font-mono text-sky-400 text-[9px]">{item[key] ?? '-'}</span>;
                    return item[key] ?? '-';
                }}
            />

            {/* Neighbors - API paginated */}
            <ApiMiniTable
                icon={Share2}
                title="Neighbors"
                iconColor="text-emerald-500"
                endpoint="/discovered_neighbors"
                query={{ asset_id: assetId, interface_type: "ethernetCsmacd" }}
                columns={[
                    { key: 'protocol', label: 'Protocol' },
                    { key: 'local_interface_name', label: 'Local' },
                    { key: 'neighbor_device_id', label: 'Device' },
                    { key: 'neighbor_platform', label: 'Platform' },
                ]}
                renderCell={(key, item) => {
                    if (key === 'protocol') {
                        return (
                            <span className={`inline-flex px-1 py-0.5 rounded text-[8px] font-bold uppercase ${item.protocol === 'lldp'
                                ? 'bg-blue-500/10 text-blue-500 border border-blue-500/20'
                                : 'bg-emerald-500/10 text-emerald-500 border border-emerald-500/20'
                                }`}>
                                {item.protocol || '-'}
                            </span>
                        );
                    }
                    if (key === 'local_interface_name') return item.local_interface_name || item.local_interface_index || '-';
                    return item[key] || '-';
                }}
            />

            {/* VLAN Configuration - API paginated */}
            <ApiMiniTable
                icon={Layers}
                title="VLANs"
                iconColor="text-purple-500"
                endpoint="/vlans"
                query={{ asset_id: assetId }}
                columns={[
                    { key: 'vlan_id', label: 'ID' },
                    { key: 'name', label: 'Name' },
                    { key: 'status', label: 'Status' },
                ]}
                renderCell={(key, item) => {
                    if (key === 'status') {
                        const isActive = item.status?.toLowerCase() === 'active';
                        return (
                            <span className={`inline-flex px-1 py-0.5 rounded text-[8px] font-bold uppercase ${isActive
                                ? 'bg-green-500/10 text-green-500 border border-green-500/20'
                                : 'bg-gray-100 dark:bg-gray-700/50 text-gray-600 dark:text-gray-500 border border-gray-300 dark:border-gray-600/20'
                                }`}>
                                {item.status || '-'}
                            </span>
                        );
                    }
                    if (key === 'vlan_id') return <span className="font-mono text-orange-400 text-[9px]">{item[key]}</span>;
                    return item[key] || '-';
                }}
            />

            {/* Switch Ports */}
            <MiniTable
                icon={Activity}
                title="Switch Ports"
                iconColor="text-cyan-500"
                columns={[
                    { key: 'port', label: 'Port' },
                    { key: 'state', label: 'State' },
                    { key: 'service', label: 'Service' },
                ]}
                data={ports}
                loading={loading.ports}
                renderCell={(key, item) => {
                    if (key === 'state') {
                        const isOpen = item.state?.toLowerCase() === 'open';
                        return (
                            <span className={`inline-flex px-1 py-0.5 rounded text-[8px] font-bold uppercase ${isOpen
                                ? 'bg-green-500/10 text-green-500 border border-green-500/20'
                                : 'bg-red-500/10 text-red-500 border border-red-500/20'
                                }`}>
                                {item.state || '-'}
                            </span>
                        );
                    }
                    return item[key] ?? '-';
                }}
            />
        </div>
    );
};

// ─── Access Point Sidebar Content ───────────────────────────────────────────────

const AccessPointSidebarContent: React.FC<{ assetId: number }> = ({ assetId }) => {
    const [overview, setOverview] = useState<any>(null);
    const [loading, setLoading] = useState(true);

    const safeParseJSON = (jsonString: string | null) => {
        if (!jsonString) return {};
        try {
            return JSON.parse(jsonString);
        } catch (e) {
            return {};
        }
    };

    useEffect(() => {
        const query = JSON.stringify({ asset_id: assetId });
        setLoading(true);
        baseapi.get('/ap_asset_detail', { params: { query } })
            .then(res => {
                if (res.data.status && res.data.data?.length > 0) {
                    setOverview(res.data.data[0]);
                } else if (res.data.id) {
                    setOverview(res.data);
                }
            })
            .catch(() => { })
            .finally(() => setLoading(false));
    }, [assetId]);

    if (loading) {
        return (
            <div className="flex justify-center py-4">
                <Loader2 className="w-4 h-4 text-orange-500 animate-spin" />
            </div>
        );
    }

    if (!overview) {
        return (
            <div className="text-center py-3">
                <div className="bg-gray-50 dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700/40 rounded-lg p-3">
                    <Info className="w-5 h-5 text-gray-400 dark:text-gray-600 mx-auto mb-1" />
                    <p className="text-[10px] text-gray-500 dark:text-gray-400 uppercase tracking-widest font-bold">No overview data available</p>
                </div>
            </div>
        );
    }

    const sysInfo = safeParseJSON(overview.system_info);
    const wireless = safeParseJSON(overview.wireless);
    const health = safeParseJSON(overview.health);
    const stations = wireless?.connected_stations || [];

    return (
        <div className="space-y-3 animate-in fade-in slide-in-from-right-1 duration-300">
            {/* Overview Grid */}
            <div className="grid grid-cols-2 gap-1.5">
                <DetailCard icon={Network} label="Vendor" value={overview.vendor} />
                <DetailCard icon={Cpu} label="Firmware" value={wireless.software_version || overview.manufacturer || "Unknown"} />
                <DetailCard icon={Clock} label="Uptime" value={sysInfo.uptime || "-"} />
                <DetailCard icon={Activity} label="Health" value={health.status || "Healthy"} status={health.status?.toLowerCase() !== 'critical'} />
            </div>

            {/* Wireless Configuration */}
            <div>
                <div className="px-2 py-1 flex items-center gap-1.5">
                    <Wifi className="w-3 h-3 text-purple-500" />
                    <h3 className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest">Wireless Setup</h3>
                </div>
                <div className="bg-gray-50 dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700/40 rounded-lg p-2.5 space-y-2">
                    <div className="flex justify-between items-center py-1 border-b border-gray-200 dark:border-gray-800/50">
                        <span className="text-[9px] font-bold text-gray-500 uppercase tracking-widest">Network SSID</span>
                        <span className="text-[11px] font-black text-purple-600 dark:text-purple-400">{wireless.ssid || "-"}</span>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <p className="text-[8px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest leading-none mb-1 text-slate-500">Frequency</p>
                            <p className="text-[10px] text-gray-800 dark:text-gray-200 font-mono font-bold">{wireless.radio?.frequency_mhz ? `${wireless.radio.frequency_mhz} MHz` : "-"}</p>
                        </div>
                        <div>
                            <p className="text-[8px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest leading-none mb-1 text-slate-500">TX Power</p>
                            <p className="text-[10px] text-gray-800 dark:text-gray-200 font-mono font-bold">{wireless.radio?.tx_power_dbm ? `${wireless.radio.tx_power_dbm} dBm` : "-"}</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Radio Metrics */}
            <div>
                <div className="px-2 py-1 flex items-center gap-1.5">
                    <Activity className="w-3 h-3 text-emerald-500" />
                    <h3 className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest">Radio Metrics</h3>
                </div>
                <div className="bg-gray-50 dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700/40 rounded-lg p-2.5">
                    <div className="grid grid-cols-3 gap-2">
                        <div className="text-center p-1.5 bg-gray-100 dark:bg-gray-900/40 rounded border border-gray-200 dark:border-gray-700/30">
                            <p className="text-[8px] text-gray-500 font-bold uppercase mb-1">Quality</p>
                            <p className="text-[10px] text-emerald-500 dark:text-emerald-400 font-black">{wireless.radio?.signal_quality || "-"}</p>
                        </div>
                        <div className="text-center p-1.5 bg-gray-100 dark:bg-gray-900/40 rounded border border-gray-200 dark:border-gray-700/30">
                            <p className="text-[8px] text-gray-500 font-bold uppercase mb-1">RSSI</p>
                            <p className="text-[10px] text-gray-800 dark:text-gray-200 font-mono font-bold">{wireless.radio?.rssi_dbm ? `${wireless.radio.rssi_dbm} dBm` : "-"}</p>
                        </div>
                        <div className="text-center p-1.5 bg-gray-100 dark:bg-gray-900/40 rounded border border-gray-200 dark:border-gray-700/30">
                            <p className="text-[8px] text-gray-500 font-bold uppercase mb-1">SNR</p>
                            <p className="text-[10px] text-gray-800 dark:text-gray-200 font-mono font-bold">{wireless.radio?.snr_db ? `${wireless.radio.snr_db} dB` : "-"}</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Interfaces Table */}
            <ApiMiniTable
                icon={Network}
                title="Interfaces"
                iconColor="text-blue-500"
                endpoint="/ap_interfaces"
                query={{ asset_id: assetId }}
                columns={[
                    { key: 'name', label: 'Name' },
                    { key: 'oper_status', label: 'Status' },
                    { key: 'mac_address', label: 'MAC' },
                ]}
                renderCell={(key, item) => {
                    if (key === 'oper_status') {
                        const isUp = item.oper_status?.toLowerCase() === 'up';
                        return (
                            <span className={`inline-flex items-center px-1 py-0.5 rounded text-[8px] font-bold uppercase shadow-sm ${isUp
                                ? 'bg-green-500/10 text-green-500 border border-green-500/20'
                                : 'bg-red-500/10 text-red-500 border border-red-500/20'
                                }`}>
                                {item.oper_status || 'Down'}
                            </span>
                        );
                    }
                    if (key === 'mac_address') return <span className="text-[9px] font-mono text-gray-500">{item[key]}</span>;
                    if (key === 'name') return <span className="text-[10px] font-bold text-gray-200">{item[key]}</span>;
                    return item[key] || '-';
                }}
            />

            {/* Connected Stations Table */}
            <MiniTable
                icon={Monitor}
                title="Connected Stations"
                iconColor="text-indigo-500"
                columns={[
                    { key: 'mac_address', label: 'MAC' },
                    { key: 'signal_dbm', label: 'Signal' },
                    { key: 'uptime', label: 'Uptime' },
                ]}
                data={stations}
                loading={false}
                renderCell={(key, item) => {
                    if (key === 'mac_address') return <span className="font-mono text-indigo-400 text-[9px] font-bold">{item[key]}</span>;
                    if (key === 'signal_dbm') return <span className="text-[10px] text-gray-300 font-mono">{item[key]}</span>;
                    return item[key] || '-';
                }}
            />
        </div>
    );
};

// ─── Port Sidebar Content ───────────────────────────────────────────────────────

const PortSidebarContent: React.FC<{ interfaceId: number }> = ({ interfaceId }) => {
    const [selectedInterface, setSelectedInterface] = useState<any>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        baseapi.get(`/discovered_interfaces`, {
            params: {
                query: JSON.stringify({ id: interfaceId })
            }
        })
            .then(res => {
                if (res.data.status && res.data.data?.length > 0) {
                    setSelectedInterface(res.data.data[0]);
                }
            })
            .catch(() => { })
            .finally(() => setLoading(false));
    }, [interfaceId]);

    if (loading) {
        return (
            <div className="flex justify-center py-12">
                <Loader2 className="w-6 h-6 text-orange-500 animate-spin" />
            </div>
        );
    }

    if (!selectedInterface) {
        return (
            <div className="text-center py-12 text-gray-500">
                <Info className="w-8 h-8 mx-auto mb-2 opacity-20" />
                <p className="text-xs">Interface details not found</p>
            </div>
        );
    }

    // Helper for rendering rows exactly as in your DiscoveredInterfaces file
    const renderDetailRow = (label: string, value: any, suffix: string = "", color: string = "text-slate-700 dark:text-slate-200") => (
        <div className="flex justify-between items-center py-1 border-b border-gray-200 dark:border-slate-800/50 last:border-0 hover:bg-gray-100 dark:hover:bg-slate-800/30 px-1 -mx-1 rounded transition-colors">
            <span className={`text-[9px] font-bold ${color} uppercase tracking-widest`}>{label}</span>
            <span className={`text-[10px] font-black ${color} text-right max-w-[65%] truncate`} title={String(value)}>
                {value !== null && value !== undefined && value !== "" ? `${value}${suffix}` : "-"}
            </span>
        </div>
    );

    return (
        <div className="space-y-3 animate-fade-in">
            {/* Header Area */}
            <div className="flex flex-col gap-2 bg-gray-50 dark:bg-slate-900/50 p-2.5 rounded-xl border border-gray-200 dark:border-slate-800">
                <div className="flex items-center gap-2">
                    <h2 className="text-sm font-bold text-gray-900 dark:text-white leading-none">
                        {selectedInterface.port_name || selectedInterface.port_number || 'Unknown Interface'}
                    </h2>
                    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[8px] font-black uppercase shadow-sm ${selectedInterface.port_status === 'up' && selectedInterface.connected_asset_id != null
                        ? 'bg-yellow-500/10 text-yellow-500 border border-yellow-500/20'
                        : selectedInterface.port_status === 'up'
                            ? 'bg-emerald-500/10 text-emerald-500 border border-emerald-500/20'
                            : 'bg-rose-500/10 text-rose-500 border border-rose-500/20'
                        }`}>
                        {selectedInterface.port_status === 'up' && selectedInterface.connected_asset_id != null
                            ? 'Used'
                            : selectedInterface.port_status}
                    </span>
                </div>

                <div className="flex items-center justify-between mt-1">
                    <span className="text-[10px] text-gray-500 dark:text-gray-400 font-mono tracking-tighter">
                        {selectedInterface.mac_address || 'No MAC Address'}
                    </span>
                    <div className="flex items-center gap-1 text-[9px] font-bold">
                        <span className="text-gray-500 dark:text-slate-400">Last Change:</span>
                        <span className="text-gray-700 dark:text-gray-300">{selectedInterface.last_change || 'Never'}</span>
                    </div>
                </div>
            </div>

            {/* Connected Asset Section (If applicable) */}
            {selectedInterface.connected_asset_name && (
                <div className="bg-blue-500/5 border border-blue-500/10 rounded-lg p-3">
                    <h3 className="text-[10px] font-bold text-blue-500 dark:text-blue-400 uppercase tracking-widest mb-2">Connected Asset</h3>
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-blue-500/20 rounded-lg">
                            <Monitor className="w-4 h-4 text-blue-500 dark:text-blue-400" />
                        </div>
                        <div>
                            <p className="text-sm font-bold text-gray-900 dark:text-white">{selectedInterface.connected_asset_name}</p>
                            <p className="text-[10px] text-gray-500">{selectedInterface.connected_asset_ip || 'No IP'}</p>
                        </div>
                    </div>
                </div>
            )}

            {/* 1. Configuration */}
            <div className="bg-gray-50 dark:bg-slate-900/50 p-3 rounded-lg border border-gray-200 dark:border-slate-800/50 shadow-inner">
                <div className="flex items-center gap-1.5 mb-2 border-b border-gray-200 dark:border-slate-800 pb-1.5">
                    <Network className="w-3.5 h-3.5 text-orange-500" />
                    <h4 className="text-orange-500 text-[10px] font-black uppercase tracking-widest">Configuration</h4>
                </div>
                <div className="space-y-0.5">
                    {renderDetailRow("Admin Status", selectedInterface.admin_status)}
                    {renderDetailRow("Port Speed", selectedInterface.port_speed)}
                    {renderDetailRow("Raw Speed (bps)", selectedInterface.raw_speed_bps)}
                    {renderDetailRow("MTU (bytes)", selectedInterface.mtu)}
                    {renderDetailRow("VLAN", selectedInterface.vlan)}
                    {renderDetailRow("Native VLAN", selectedInterface.native_vlan)}
                </div>
            </div>

            {/* 2. Traffic & Packets */}
            <div className="bg-gray-50 dark:bg-slate-900/50 p-3 rounded-lg border border-gray-200 dark:border-slate-800/50 shadow-inner">
                <div className="flex items-center gap-1.5 mb-2 border-b border-gray-200 dark:border-slate-800 pb-1.5">
                    <Activity className="w-3.5 h-3.5 text-sky-500" />
                    <h4 className="text-sky-500 text-[10px] font-black tracking-widest">TRAFFIC & PACKETS (Rx/Tx)</h4>
                </div>

                <div className="space-y-4">
                    {/* INBOUND */}
                    <div className="space-y-0.5">
                        <h5 className="text-[9px] font-bold text-sky-500 dark:text-sky-400 uppercase tracking-widest mb-1 border-b border-gray-200 dark:border-slate-800 pb-1">Inbound (RX)</h5>
                        {renderDetailRow("RX Traffic", formatBytes(selectedInterface.in_octets))}
                        {renderDetailRow("RX Unicast Packets", selectedInterface.in_unicast_packets?.toLocaleString())}
                        {renderDetailRow("RX Non-Unicast Packets", selectedInterface.in_non_unicast_packets?.toLocaleString())}
                        {renderDetailRow("Rx Errors", selectedInterface.in_errors?.toLocaleString(), "", "text-rose-500 dark:text-rose-400/90")}
                        {renderDetailRow("Rx Discards", selectedInterface.in_discards?.toLocaleString(), "", "text-rose-500 dark:text-rose-400/90")}
                        {renderDetailRow("Loss Rx", selectedInterface.packet_loss_in_loss_percentage, "%", "text-rose-500 dark:text-rose-400/90")}
                    </div>

                    {/* OUTBOUND */}
                    <div className="space-y-0.5">
                        <h5 className="text-[9px] font-bold text-sky-500 dark:text-sky-400 uppercase tracking-widest mb-1 border-b border-gray-200 dark:border-slate-800 pb-1">Outbound (TX)</h5>
                        {renderDetailRow("TX Traffic", formatBytes(selectedInterface.out_octets))}
                        {renderDetailRow("TX Unicast Packets", selectedInterface.out_unicast_packets?.toLocaleString())}
                        {renderDetailRow("TX Non-Unicast Packets", selectedInterface.out_non_unicast_packets?.toLocaleString())}
                        {renderDetailRow("Tx Errors", selectedInterface.out_errors?.toLocaleString(), "", "text-rose-500 dark:text-rose-400/90")}
                        {renderDetailRow("Tx Discards", selectedInterface.out_discards?.toLocaleString(), "", "text-rose-400/90")}
                        {renderDetailRow("Loss Tx", selectedInterface.packet_loss_out_loss_percentage, "%", "text-rose-500 dark:text-rose-400/90")}
                    </div>
                </div>
            </div>

            {/* 3. System Metadata */}
            <div className="bg-gray-50 dark:bg-slate-900/50 p-3 rounded-lg border border-gray-200 dark:border-slate-800/50 shadow-inner">
                <div className="flex items-center gap-1.5 mb-2 border-b border-gray-200 dark:border-slate-800 pb-1.5">
                    <Info className="w-3.5 h-3.5 text-slate-500 dark:text-slate-400" />
                    <h4 className="text-slate-600 dark:text-slate-400 text-[10px] font-black uppercase tracking-widest">System Metadata</h4>
                </div>
                <div className="space-y-0.5">
                    {renderDetailRow("Index", selectedInterface.port_index)}
                    {renderDetailRow("Interface Type", selectedInterface.interface_type)}
                    {renderDetailRow("Conn Type", selectedInterface.connection_type)}

                    <div className="mt-2 pt-2 border-t border-gray-200 dark:border-slate-800/50">
                        <span className="text-[9px] font-bold text-gray-700 dark:text-slate-200 uppercase tracking-widest block mb-1">Description</span>
                        <div className="bg-white dark:bg-slate-950/60 p-2.5 rounded border border-gray-200 dark:border-slate-800 text-[10px] text-gray-700 dark:text-slate-300 font-medium min-h-[40px] break-words">
                            {selectedInterface.port_description && selectedInterface.port_description !== '""' ? selectedInterface.port_description : "No description provided"}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

// ─── Main Sidebar Component ─────────────────────────────────────────────────────

const AssetDetailSidebar: React.FC<AssetDetailSidebarProps> = ({
    open,
    assetId,
    assetName,
    assetType,
    assetIp,
    onClose,
}) => {
    const level = useClientLevel();
    const client_id = level?.id;

    const handleMarkPrimary = async () => {
        try {
            const response = await baseapi.post('topology/mark-primary', {
                asset_id: assetId,
                client_id: client_id,
                asset_type: assetType
            });
            if (response.data.status) {
                showSuccess(response.data.message || "Marked as primary successfully");
            } else {
                showError(response.data.message || "Failed to mark as primary");
            }
        } catch (error: any) {
            showError(error.message);
        }
    };
    const Icon = getAssetIcon(assetType);

    const isSwitchType = assetType === 'switch';
    const isFirewallType = assetType === 'firewall';
    const isCameraType = assetType === 'camera';
    const isAccessPointType = assetType === 'access_point';
    const isPort = assetType === 'interface';

    const typeLabel = assetType.charAt(0).toUpperCase() + assetType.slice(1).replace(/_/g, ' ');

    return (
        <div
            className={`h-full border-l border-gray-200 dark:border-gray-800 bg-white dark:bg-[#0d1017] transition-all duration-300 ease-in-out flex flex-col shadow-2xl ${open ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0 pointer-events-none absolute'
                }`}
            style={{ width: open ? '380px' : '0px', minWidth: open ? '380px' : '0px' }}
        >
            <div className="h-full flex flex-col min-w-[380px]">
                {/* Header */}
                <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-800 flex-shrink-0 bg-gray-50/50 dark:bg-transparent">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2.5 min-w-0">
                            <div className={`p-1.5 rounded-lg shadow-lg ${isCameraType
                                ? 'bg-orange-600 shadow-orange-600/20'
                                : isSwitchType
                                    ? 'bg-blue-600 shadow-blue-600/20'
                                    : isFirewallType
                                        ? 'bg-red-600 shadow-red-600/20'
                                        : isAccessPointType
                                            ? 'bg-purple-600 shadow-purple-600/20'
                                            : 'bg-slate-600 shadow-slate-600/20'
                                }`}>
                                <Icon className="w-4 h-4 text-white" />
                            </div>
                            <div className="min-w-0">
                                <h2
                                    className="text-sm font-bold text-gray-900 dark:text-white leading-tight max-w-[180px] break-words"
                                    title={assetName}
                                >
                                    {assetName}
                                </h2>
                                <div className="flex items-center gap-2 mt-0.5">
                                    <span className={`px-1.5 py-0.5 rounded text-[8px] font-bold uppercase tracking-wider ${isCameraType
                                        ? 'bg-orange-500/10 text-orange-400 border border-orange-500/20'
                                        : isSwitchType
                                            ? 'bg-blue-500/10 text-blue-400 border border-blue-500/20'
                                            : isFirewallType
                                                ? 'bg-red-500/10 text-red-400 border border-red-500/20'
                                                : isAccessPointType
                                                    ? 'bg-purple-500/10 text-purple-400 border border-purple-500/20'
                                                    : 'bg-slate-500/10 text-slate-400 border border-slate-500/20'
                                        }`}>
                                        {typeLabel}
                                    </span>
                                    <span className="text-[10px] text-gray-500 font-mono">{assetIp}</span>
                                </div>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            {(isSwitchType || isFirewallType) && (
                                <button
                                    onClick={handleMarkPrimary}
                                    className="px-2 py-1 rounded text-[10px] font-bold uppercase bg-orange-600/20 text-orange-600 dark:text-orange-400 border border-orange-500/30 hover:bg-orange-600 hover:text-white transition-all active:scale-95"
                                >
                                    Is Primary
                                </button>
                            )}
                            <button
                                onClick={onClose}
                                className="p-1.5 rounded-lg bg-gray-100 dark:bg-gray-800 border border-gray-300 dark:border-gray-700 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-200 dark:hover:bg-gray-700 transition-all active:scale-95"
                            >
                                <X className="w-3.5 h-3.5" />
                            </button>
                        </div>
                    </div>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto custom-scroll p-3 space-y-3">
                    {isCameraType && <CameraSidebarContent client_id={client_id} assetId={assetId} />}
                    {isSwitchType && <SwitchSidebarContent assetId={assetId} />}
                    {isFirewallType && <FirewallSidebarContent assetId={assetId} client_id={client_id} />}
                    {isAccessPointType && <AccessPointSidebarContent assetId={assetId} />}
                    {isPort && <PortSidebarContent interfaceId={assetId} />}
                    {!isCameraType && !isSwitchType && !isFirewallType && !isAccessPointType && !isPort && (
                        <div className="flex flex-col items-center justify-center py-12 text-center">
                            <Icon className="w-10 h-10 text-gray-400 dark:text-gray-600 mb-3" />
                            <p className="text-sm font-bold text-gray-700 dark:text-gray-400 mb-1">Asset Details</p>
                            <p className="text-[10px] text-gray-500 dark:text-gray-600 max-w-[250px]">
                                Detailed scan data is available for Camera, Switch, and Firewall type assets.
                            </p>
                            <div className="mt-4 grid grid-cols-2 gap-2 w-full max-w-[280px]">
                                <DetailCard icon={Box} label="Type" value={typeLabel} />
                                <DetailCard icon={Globe} label="IP Address" value={assetIp} />
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AssetDetailSidebar;
